import styles from './ToolBar.module.scss';

import { TableRows } from '@mui/icons-material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Theme, useMediaQuery } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { Row, Table } from '@tanstack/react-table';
import {
  type JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  FilterType,
  FilterValue,
  prepareFilters,
  RowsLength,
} from '@work4all/components';
import { ConvertPopover } from '@work4all/components/lib/components/convert-popover/ConvertPopover';
import { useTablePrefilterContext } from '@work4all/components/lib/components/table/TablePrefilterProvider';
import { useTableVariant } from '@work4all/components/lib/components/table/TableVariantProvider';
import { ActionsBar } from '@work4all/components/lib/dataDisplay/actions-bar/ActionsBar';
import { useToolbarCustomActions } from '@work4all/components/lib/dataDisplay/actions-bar/hooks/use-toolbar-custom-actions';
import {
  ButtonWithLink,
  CustomToolbar,
  DisabledParams,
} from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';
import { genFilterTitleV8 } from '@work4all/components/lib/dataDisplay/basic-table/utils/genFilterTitleV8';
import {
  SortAscIcon,
  SortDescIcon,
  ViewColumnSelectedIcon,
} from '@work4all/components/lib/icons';
import { IActionConfig } from '@work4all/components/lib/input/actions/types';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useNavigate } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { ListPageContext } from '../../../../../containers/file-entities-lists/list-page-context';
import { HomeWidgetsContext } from '../../../../../containers/home/components/home-widgets/HomeWidgetsContextProvider';
import { settings, useSetting } from '../../../../../settings';
import { HomeWidgetType } from '../../../../../settings/validation/HomeWidget';
import { stringifyTableSettings } from '../../../make-table-settings';
import { useTableLayoutState } from '../../../table-layout';
import { TableFilterV8 } from '../../TableFilter';
import { isBoardEnabled, isCardEnabled } from '../kanban-board/Board';
import { WithBubble } from '../with-bubble/WithBubble';

import { ActionsBarMobileV8 } from './components/actions-bar-mobile/ActionsBarMobileV8';
import { ActionConfig as ActionsMenuMobileActionConfig } from './components/actions-bar-mobile/ActionsMenuMobile';
import { ColumnsVisibilityV8 } from './components/columns-visibility/ColumnsVisibilityV8';
import { FiltersOverlayV8 } from './components/filters/filters-overlay/FiltersOverlayV8';
import { SelectedFiltersList } from './components/filters/selected-filters-list/SelectedFiltersList';
import { ToolbarFilterPickerContext } from './components/filters/ToolbarFilterPickerContext';
import { GroupingsVisibiltyV8 } from './components/groupings/GroupingsVisibiltyV8';
import { SelectedGroupsListV8 } from './components/groupings/selected-groups-list/SelectedGroupsListV8';
import { LayoutAction } from './components/layout-action/LayoutAction';
import { SearchActionV8 } from './components/search-action/SearchActionV8';
import { SortColumnPopoverV8 } from './components/SortColumnPopoverV8';
import { TableLayoutPopover } from './components/TableLayoutPopover';
import { TableSettingsOverlay } from './components/TableSettingsOverlay';
import { useToolbarDeleteAction } from './hooks/use-toolbar-delete-action';
import { RemoveToolbarAction } from './type';

interface ConvertToolbarAction extends DisabledParams {
  exclude: Entities;
  handler: (entity: Entities) => void;
}

export interface ToolbarV8Props {
  mobile?: boolean;
  hideLayoutSelect?: boolean;
  hideMoreButton?: boolean;
  listEntityType?: Entities;
  exportCSV?: JSX.Element;
  actions?: {
    add?: ButtonWithLink;
    edit?: ButtonWithLink<string> | null;
    remove?: RemoveToolbarAction | null;
    createWidget?: boolean;
    convert?: ConvertToolbarAction | null;
    transform?: ((ids: string[]) => void) | null;
    export?: ((ids: string[]) => void) | null;
    resetColumns?: () => void;
    custom?: CustomToolbar;
    openInNewTab?: {
      getPath: (id: string) => string;
    };
    /**
     * Default: true.
     */
    groupingEnabled?: boolean;
    clone?: {
      handler: () => void;
      disabled: boolean;
    };
  };
  hideSearch?: boolean;
  table: Table<unknown>;
  total?: number | RowsLength;
}

export function ToolbarV8(props: ToolbarV8Props) {
  const {
    actions = {},
    mobile = false,
    hideLayoutSelect = false,
    hideMoreButton = false,
    hideSearch = false,
    listEntityType,
    table,
  } = props;
  const { t } = useTranslation();
  const listPageContext = useContext(ListPageContext);

  const tableState = table.getState();

  const homeWidgetsContext = useContext(HomeWidgetsContext);
  const addWidget = homeWidgetsContext?.addWidget;
  const closedEntitiesSetting = useSetting(
    settings.hideClosedEntities({ entityType: listPageContext.entityType })
  );

  const setHideClosedEntitiesSetting = useEventCallback((value: boolean) => {
    closedEntitiesSetting.set(value);
  });

  const [filterPickerActive, setFilterPickerActive] = useState(false);

  const columns = table.getAllFlatColumns();

  const getChips = useCallback(() => {
    const filtersById = Object.fromEntries(
      tableState.columnFilters.map((filter) => [filter.id, filter])
    );

    return (
      columns
        // Filter non-selected and primary filters
        .filter((col) => col.columnDef.isPrimaryFilter || !!filtersById[col.id])
        // Move all selected filters before empty ones.
        .sort((a, b) => {
          if (filtersById[a.id] && !filtersById[b.id]) {
            return -1;
          }
          return 0;
        })
        .map((col) => {
          const filterValue = filtersById[col.id]?.value as any;

          return {
            id: col.id,
            title: !filterValue
              ? (col.columnDef.header as string)
              : genFilterTitleV8(col),
            readOnly: !!filterValue?.readOnly,
            isPrimaryFilter: !!col.columnDef.isPrimaryFilter,
            renderFilter: (item) => (
              <TableFilterV8
                column={col}
                listEntityType={listEntityType}
                columnFilters={tableState.columnFilters}
              >
                {item}
              </TableFilterV8>
            ),
            handleDelete: !filterValue
              ? undefined
              : () => {
                  if (col.columnDef.filterType === FilterType.ClosedStatus) {
                    setHideClosedEntitiesSetting(false);
                  }
                  col.setFilterValue(null);
                },
          };
        })
        .filter(Boolean)
        // Move all read-only filter to the beginning of the list.
        .sort((a, b) => {
          if (a.readOnly && !b.readOnly) {
            return -1;
          }
          return 0;
        }) ?? []
    );
  }, [columns, listEntityType, setHideClosedEntitiesSetting, tableState]);

  const [chips, setChips] = useState(getChips);

  const groupingsPopoverState = usePopoverState();
  const columnsPopoverState = usePopoverState();
  const convertPopoverState = usePopoverState(mobile ? 'top' : 'bottom');
  const sortColumnPopoverState = usePopoverState(mobile ? 'top' : 'bottom');
  const layoutPopoverState = usePopoverState(mobile ? 'top' : 'bottom');

  const settingsRootRef = useRef<HTMLDivElement | HTMLButtonElement>(null);
  const filtersRootRef = useRef<HTMLDivElement | HTMLButtonElement>(null);
  const groupingsRootRef = useRef<HTMLDivElement | HTMLButtonElement>(null);
  const columnsRootRef = useRef<HTMLDivElement | HTMLButtonElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const desktopMenuButtonRef = useRef<HTMLButtonElement>(null);

  const [showSettingsOverlay, setSettingsOverlay] = useState(false);
  const [showFilterOverlay, setFilterOverlay] = useState(false);

  const groupedRowModel = table.getGroupedRowModel();
  const total =
    props.total === 'rowsLength'
      ? groupedRowModel.rows.reduce(
          (acc: number, item) =>
            acc + ((item.original as Row<any>)?.meta?.groupByTotalCount || 0),
          0
        )
      : props.total;

  const selectedRows = table.getSelectedRowModel().flatRows;
  const selectedRowsIdsList = useMemo(() => {
    return selectedRows.map((row) => row.id);
  }, [selectedRows]);

  const openColumnsPopover = columnsPopoverState.handleClick;
  const openConvertPopover = convertPopoverState.handleClick;
  const openGroupingPopover = groupingsPopoverState.handleClick;
  const openSortColumnPopover = sortColumnPopoverState.handleClick;
  const openLayoutPopover = layoutPopoverState.handleClick;

  const navigate = useNavigate();

  const searchFilter = tableState.globalFilter;
  const handleSearchFilter = useCallback(
    (value: string) => {
      table.setGlobalFilter(value);
    },
    [table]
  );

  const customMappedActions = useToolbarCustomActions({
    selectedRowsIdsList,
    custom: props?.actions?.custom,
  });
  const billingAction = customMappedActions.left.find(
    (a) => a.actionKey === 'Billing'
  );

  const [layoutValue] = useTableLayoutState();

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );
  const { prefilter } = useTablePrefilterContext();
  const { variant } = useTableVariant();

  const { onDeleteClick } = useToolbarDeleteAction({
    ids: selectedRowsIdsList,
    action: actions.remove,
  });

  const canFilter = useMemo(() => {
    return columns.some((column) => column.getCanFilter());
  }, [columns]);

  const canGroup = useMemo(
    () => columns.some((col) => col.getCanGroup()),
    [columns]
  );

  const mappedActions = useMemo(() => {
    const isSomethingSelected = selectedRowsIdsList.length > 0;
    const isExactlyOneSelected = selectedRowsIdsList.length === 1;

    const add = actions.add;
    const edit = actions.edit;
    const remove = actions.remove;
    const convert = actions.convert;
    const exportAction = actions.export;
    const transform = actions.transform;
    const createWidget = actions.createWidget;
    const clone = actions.clone;
    const billing = billingAction;
    const config: Record<
      | 'add'
      | 'edit'
      | 'filter'
      | 'columns'
      | 'remove'
      | 'convert'
      | 'group'
      | 'export'
      | 'go'
      | 'search'
      | 'sortColumn'
      | 'sortOrder'
      | 'layout'
      | 'createWidget'
      | 'clone'
      | 'settings'
      | 'billing',
      (IActionConfig & ActionsMenuMobileActionConfig) | null
    > = {
      add: add
        ? add.getHref
          ? {
              href: add.getHref(),
              dataTestId: `add-${listPageContext.entityType}`,
            }
          : {
              onClick: () => add.handler(),
              disabled: add.disabled,
              disableReason: add.disableReason,
              rootRef: add.rootRef,
            }
        : null,
      edit:
        edit && isExactlyOneSelected
          ? edit.getHref
            ? {
                href: edit.getHref(cleanIds(selectedRowsIdsList)[0]),
                disabled: edit.disabled,
                disableReason: edit.disableReason,
              }
            : {
                onClick: () => {
                  edit.handler(cleanIds(selectedRowsIdsList)[0]);
                },
                disableReason: edit.disableReason,
                disabled: edit.disabled,
              }
          : null,

      filter: columns.find((x) => x.getCanFilter())
        ? {
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              sendAmplitudeData(EventType.TabFilter, {
                name: listPageContext.entityType,
              });
              setFilterOverlay(true);
            },
            children: <SelectedFiltersList list={chips} />,
            rootRef: filtersRootRef,
          }
        : null,

      settings: {
        onClick: () => {
          setSettingsOverlay(true);
        },
        rootRef: settingsRootRef,
        label: t('SETTINGS.SETTINGS'),
      },

      columns: {
        onClick: (event) => {
          return openColumnsPopover(
            event,
            (mobile ? mobileMenuButtonRef : desktopMenuButtonRef).current
          );
        },
        rootRef: columnsRootRef,
        label: layoutValue === 'cards' ? t('MASK.ROWS') : undefined,
        icon: layoutValue === 'cards' ? <TableRows /> : undefined,
      },

      remove:
        remove && isSomethingSelected
          ? {
              onClick: onDeleteClick,
              disabled: remove.disabled,
              disableReason: remove.disableReason,
              dataTestId: `delete-${listPageContext.entityType}`,
            }
          : null,

      convert:
        convert && isSomethingSelected
          ? {
              onClick: (event) => {
                openConvertPopover(event);
              },
              margin: 'lg',
            }
          : null,

      group:
        canGroup &&
        (actions.groupingEnabled === undefined || actions.groupingEnabled)
          ? {
              onClick: (event) => {
                openGroupingPopover(
                  event,
                  (mobile ? mobileMenuButtonRef : desktopMenuButtonRef).current
                );
              },
              margin: 'lg',
              rootRef: groupingsRootRef,
              children: (
                <WithBubble count={tableState.grouping.length}>
                  <SelectedGroupsListV8 table={table} />
                </WithBubble>
              ),
            }
          : undefined,

      export:
        exportAction && isSomethingSelected
          ? {
              onClick: () => {
                exportAction(selectedRowsIdsList);
              },
            }
          : null,

      go:
        transform && isSomethingSelected
          ? {
              onClick: () => {
                transform(selectedRowsIdsList);
              },
            }
          : null,

      search: {
        onClick: () => {
          console.warn("This shouldn't be used");
        },
        render: () =>
          hideSearch ? null : (
            <SearchActionV8
              value={searchFilter}
              onChange={handleSearchFilter}
            />
          ),
      },
      sortColumn: (() => {
        const sortByColumn =
          tableState.sorting.length > 0
            ? columns.find((column) => column.id === tableState.sorting[0].id)
            : null;

        const columnOrRow = layoutValue === 'cards' ? 'row' : 'column';
        const label: string = sortByColumn
          ? (t(`COMMON.SORT_${columnOrRow.toUpperCase()}`, {
              [columnOrRow]: sortByColumn.columnDef.header,
            }) as string)
          : t(`COMMON.SORT_${columnOrRow.toUpperCase()}_EMPTY`);

        return {
          onClick: (event) => {
            openSortColumnPopover(event, mobileMenuButtonRef.current);
          },
          label: label,
          icon: <ViewColumnSelectedIcon />,
        };
      })(),

      sortOrder:
        tableState.sorting.length > 0
          ? {
              onClick: () => {
                table.setSorting([
                  {
                    id: tableState.sorting[0].id,
                    desc: !tableState.sorting[0].desc,
                  },
                ]);
              },
              label: tableState.sorting[0].desc
                ? t('COMMON.SORT_ORDER_DESC')
                : t('COMMON.SORT_ORDER_ASC'),
              icon: tableState.sorting[0].desc ? (
                <SortDescIcon />
              ) : (
                <SortAscIcon />
              ),
            }
          : null,

      layout:
        !hideLayoutSelect && isCardEnabled(listEntityType)
          ? {
              onClick: (event) => {
                openLayoutPopover(
                  event,
                  (mobile ? mobileMenuButtonRef : desktopMenuButtonRef).current
                );
              },
              render: mobile
                ? undefined
                : () => (
                    <LayoutAction
                      isBoardEnabled={isBoardEnabled(listEntityType)}
                      isCardEnabled={isCardEnabled(listEntityType)}
                    />
                  ),
              label: t('COMMON.LAYOUT_MODE'),
              icon: <RemoveRedEyeIcon />,
            }
          : undefined,

      createWidget:
        createWidget &&
        !isMobile &&
        listPageContext.entityType !== Entities.position
          ? {
              onClick: () => {
                // Define a proper CustomFilterMapping with correct typing
                const customDateFilterMapping = {
                  [FilterType.Date]: (): [string, any][] => {
                    // Date can be a relative type (e.g.'today'), so we should not transform it
                    // The relative filter will be preserved in customFilters which will be converted
                    // to query directly in the EntityWidget component to keep the relative date reference
                    return [];
                  },
                };

                // prepared filters - ready to use in query directly
                const { filter } = prepareFilters(
                  tableState.columnFilters,
                  tableState.sorting,
                  customDateFilterMapping,
                  listPageContext.entityType
                );

                const dateFilter = tableState.columnFilters?.filter(
                  (f) =>
                    (f.value as FilterValue & { filterType: FilterType })
                      .filterType === FilterType.Date
                );
                const customFilters = [...dateFilter];

                // We do not pass 'customFilters' as 'filters' in the definition directly to
                // not override 'filter' (see: getEntityWidgetFilter() in useEntityWidgetFilterAndSearchParams.tsx)
                addWidget({
                  id: new Date().toISOString(),
                  type: HomeWidgetType.entityWidget,
                  definition: {
                    entity: listPageContext.entityType,
                    filter,
                    customFilters,
                    prefilter,
                    variant,
                    tableUrl: stringifyTableSettings({
                      filter:
                        tableState.columnFilters.map((filter) => {
                          return {
                            ...filter,
                            value: (filter?.value as any)?.value,
                          };
                        }) || [],
                    }),
                  },
                });
                navigate('/home');
              },
            }
          : null,

      clone:
        clone && isSomethingSelected
          ? { onClick: clone.handler, disabled: clone.disabled }
          : null,

      billing:
        billing && isSomethingSelected
          ? { onClick: billing.onClick, disabled: billing.disabled }
          : null,
    };

    return config;
  }, [
    selectedRowsIdsList,
    actions.add,
    actions.edit,
    actions.remove,
    actions.convert,
    actions.export,
    actions.transform,
    actions.createWidget,
    actions.clone,
    actions.groupingEnabled,
    billingAction,
    listPageContext.entityType,
    columns,
    chips,
    t,
    layoutValue,
    onDeleteClick,
    tableState,
    table,
    hideLayoutSelect,
    mobile,
    isMobile,
    openColumnsPopover,
    openConvertPopover,
    openGroupingPopover,
    hideSearch,
    searchFilter,
    handleSearchFilter,
    openSortColumnPopover,
    openLayoutPopover,
    listEntityType,
    addWidget,
    prefilter,
    variant,
    navigate,
    canGroup,
  ]);

  useEffect(() => {
    if (!filterPickerActive) {
      const chips = getChips();
      setChips(chips);
    }
  }, [tableState.columnFilters, filterPickerActive, getChips]);

  return (
    <ToolbarFilterPickerContext.Provider
      value={{
        setFilterPickerActive,
        filterPickerActive,
      }}
    >
      {showFilterOverlay && (
        <FiltersOverlayV8
          table={table}
          open={showFilterOverlay}
          onClose={() => setFilterOverlay(false)}
          listEntityType={listEntityType}
        />
      )}
      <TableSettingsOverlay
        entityType={listPageContext.entityType}
        isOpen={showSettingsOverlay}
        close={() => setSettingsOverlay(false)}
      />
      <GroupingsVisibiltyV8
        table={table}
        popoverState={groupingsPopoverState}
      />
      <ColumnsVisibilityV8
        table={table}
        popoverState={columnsPopoverState}
        title={layoutValue === 'cards' ? t('MASK.ROWS') : undefined}
        onResetColumns={actions.resetColumns}
      />
      <SortColumnPopoverV8
        table={table}
        popoverState={sortColumnPopoverState}
      />
      <TableLayoutPopover popoverState={layoutPopoverState} />

      {actions.convert ? (
        <ConvertPopover
          exclude={actions.convert.exclude}
          onClick={(entity) => {
            actions.convert.handler(entity);
            convertPopoverState.onClose();
          }}
          popoverState={convertPopoverState}
        />
      ) : null}

      {mobile ? (
        <ActionsBarMobileV8
          mappedActions={mappedActions}
          searchFilter={searchFilter}
          onSearchFilter={handleSearchFilter}
          canFilter={canFilter}
          openFilterOverlay={() => setFilterOverlay(true)}
          isSomeRowsSelected={table.getIsSomeRowsSelected()}
          onResetSelectedRows={() => table.resetRowSelection()}
          filtersCount={chips.length}
          mobile={mobile}
          hideMoreButton={hideMoreButton}
          hideSearch={hideSearch}
          mobileMenuButtonRef={mobileMenuButtonRef}
        />
      ) : (
        <div className={styles.root}>
          <ActionsBar
            margin="xs"
            {...mappedActions}
            isGrouped={tableState.grouping.length > 0}
            menuButtonRef={desktopMenuButtonRef}
            customActions={customMappedActions}
            hideMoreButton={hideMoreButton}
            totalRecordsAvailable={total}
          />
        </div>
      )}
    </ToolbarFilterPickerContext.Provider>
  );
}

export const cleanIds = (ids: string[]) => {
  return ids.filter(Boolean).map((id) => {
    if (!id.includes('.')) return id;
    const parts = id.split('.');
    return parts[parts.length - 1];
  });
};
